import { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  PageSection,
  PermissionButton,
  StackablePageButtons,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import {
  UserResetMFADialogWithButton,
  UserSendPasswordResetEmailDialogWithButton,
  UserUpdatePasswordDialogWithButton,
} from '@product-framework/components/Dialogs';
import { useNavigate } from 'react-router-dom';
import { useServiceModule } from '../../../../../providers/ServiceModuleProvider';
import ServiceProviderLayout from '../../../components/ServiceProviderLayout';
import getSPLevelStackableButtons from '../../../utils/getSPLevelStackableButtons';
import schema from './edit.json';
import validationSchema from './validations';
import {
  useServiceProvider,
  withServiceProvider,
} from '../../../../../providers/ServiceProviderProvider';

const FORM_ID = 'ServiceProviderUserEditForm';
const PERMISSION_SP = 'SERVICEPROVIDERUSER';

function UserEdit() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const store = useStore();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, get, getApiUrl } = useDataProvider(provider);
  const [initialValues, setInitialValues] = useState({});
  const { moduleSettings } = useServiceModule();
  const { serviceProviderId, userId, refreshData } = useServiceProvider();

  const onChange = () => {
    refreshData({
      refreshProfileWarnings: true,
      refreshUser: true,
    });
  };

  const fetchDefaultValues = useCallback(async () => {
    const url = `${getApiUrl()}/sxcl/v1/users/${userId}`;
    const [response] = await Promise.all([get(url)]);
    if (response?.ok) {
      const result = {
        data: {
          ...response.data,
          role: {
            value: response?.data?.role?.id,
            label: response?.data?.role?.roleIdentifier,
          },
          insightPurchased: moduleSettings.insightPurchased || false,
        },
        errors: null,
      };
      setInitialValues(result.data);
      return result;
    }

    let errors = [];
    if (response?.errors) {
      errors = [...response.errors];
    }

    return {
      data: null,
      errors,
    };
  }, [userId]);

  const onSubmit = async formData => {
    const { disabledReason, ...data } = formData;
    const url = `${getApiUrl()}/sxcl/v1/users/${userId}`;
    const requestData = {
      email: data?.email,
      firstName: data?.firstName,
      lastName: data?.lastName,
      enabled: data?.enabled === 'true',
      userRoleId: data?.role?.value,
      phoneNumber: data?.phoneNumber,
      phoneNumberType: data?.phoneNumberType,
      smsOptedIn: [true, 'true'].includes(data?.smsOptedIn),
    };
    const response = await put(url, requestData);

    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'USER_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'User was saved successfully',
        }),
      );

      const nextValues = {
        ...response.data,
        role: {
          value: response.data.role.id,
          label: response.data.role.roleIdentifier,
        },
        insightPurchased: moduleSettings.insightPurchased,
      };

      return {
        nextValues,
        errors: null,
        callback: () => {
          setInitialValues(nextValues);
          onChange();
        },
      };
    }
    return {
      nextValues: data,
      errors: response.errors,
    };
  };

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'LIST_USERS',
        defaultMessage: 'List Users',
      }),
      Icon: IconLibrary.User,
      permissionName: 'CAN_LIST_SP_USER',
      variant: 'outlined',
      onClick: () => {
        store.domain.list.reset('ServiceProviderUserList');
        navigate(`/serviceproviders/edit/${serviceProviderId}/users`);
      },
    },
  ];

  return (
    <ServiceProviderLayout
      selectedStepperId="USERS"
      stackablePageButtons={getSPLevelStackableButtons({
        navigate,
      })}>
      <PageSection
        containerProps={{ sx: { mb: 3 } }}
        HeaderContent={
          <StackablePageButtons
            backButton={false}
            buttons={stackablePageButtons}
          />
        }
        label={formatMessage({
          id: 'EDIT_USER',
          defaultMessage: 'Edit User',
        })}>
        <Form
          formId={FORM_ID}
          ref={formRef}
          permissionEntities={[PERMISSION_SP]}
          onSubmit={onSubmit}
          schema={schema}
          fetchDefaultValues={fetchDefaultValues}
          validationSchema={validationSchema(formatMessage, getApiUrl, get)}>
          <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
            <PermissionButton
              permissionName="CAN_UPDATE_SP_USER"
              variant="contained"
              startIcon={<IconLibrary.Save />}
              color="primary"
              onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
              {formatMessage({
                id: 'SAVE_USER',
                defaultMessage: 'Save User',
              })}
            </PermissionButton>
            <PermissionButton
              permissionName="CAN_UPDATE_SP_USER"
              variant="contained"
              color="primary"
              startIcon={<IconLibrary.Reset />}
              onClick={() => formRef.current?.reset()}>
              {formatMessage({
                id: 'RESET',
                defaultMessage: 'Reset',
              })}
            </PermissionButton>
            <UserUpdatePasswordDialogWithButton
              email={initialValues?.email}
              firstName={initialValues?.firstName}
              lastName={initialValues?.lastName}
              formRef={formRef}
              permissionName="CAN_UPDATE_SP_PASSWORD"
            />
            <UserResetMFADialogWithButton
              email={initialValues?.email}
              firstName={initialValues?.firstName}
              lastName={initialValues?.lastName}
              formRef={formRef}
              permissionName="CAN_RESET_SP_USER_MFA"
            />
            <UserSendPasswordResetEmailDialogWithButton
              email={initialValues?.email}
              firstName={initialValues?.firstName}
              lastName={initialValues?.lastName}
              formRef={formRef}
              permissionName="CAN_SEND_SP_RESET_PASSWORD_EMAIL"
            />
          </Box>
        </Form>
      </PageSection>
    </ServiceProviderLayout>
  );
}

const UserEditObservable = observer(UserEdit);
export default withServiceProvider(UserEditObservable);
